import { Link } from "react-router-dom";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { features } from "@/lib/features";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PersonaRow } from "@/components/experience/persona-row";
import { CanEdit } from "@/lib/project-access";
import type { PersonaListItem } from "./personas-tab-types";

interface PersonasTableProps {
  personas: PersonaListItem[];
  resolvedProjectId: string;
  canEdit: boolean;
  onEdit: (persona: PersonaListItem) => void;
  onDelete: (persona: { id: string; name: string }) => void;
}

export function PersonasTable({
  personas,
  resolvedProjectId,
  canEdit,
  onEdit,
  onDelete,
}: PersonasTableProps) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground select-none">
        <div className="size-8 shrink-0" />
        <div className="flex-1 min-w-0 lg:w-56 lg:flex-initial">
          Persona & Role
        </div>
        <div className="flex-1 min-w-0 hidden lg:block">
          Perspective & Background
        </div>
        <div className="w-16 lg:w-24 shrink-0 text-center">Status</div>
        <div className="w-10 lg:w-32 shrink-0 text-right">Actions</div>
      </div>

      <div className="divide-y divide-border">
        {personas.map((persona) => (
          <PersonaRow
            key={persona.id}
            variant="full"
            href={`/projects/${resolvedProjectId}/personas/${persona.id}`}
            persona={persona}
            actions={
              <>
                {features.liveWalkthrough && canEdit && (
                  <Button
                    asChild
                    variant="ghost"
                    size="xs"
                    className="hidden lg:inline-flex h-7 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Link
                      to={`/projects/${resolvedProjectId}/live-walkthrough?personaId=${persona.id}`}
                    >
                      Launch →
                    </Link>
                  </Button>
                )}

                <CanEdit>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        className="size-7 text-muted-foreground"
                        aria-label={`Actions for ${persona.name}`}
                      >
                        <MoreHorizontal className="size-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {features.liveWalkthrough && (
                        <DropdownMenuItem asChild className="lg:hidden">
                          <Link
                            to={`/projects/${resolvedProjectId}/live-walkthrough?personaId=${persona.id}`}
                          >
                            Launch walkthrough →
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => onEdit(persona)}>
                        <Pencil className="mr-2 size-3.5" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() =>
                          onDelete({
                            id: persona.id,
                            name: persona.name,
                          })
                        }
                      >
                        <Trash2 className="mr-2 size-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CanEdit>
              </>
            }
          />
        ))}
      </div>
    </div>
  );
}
